/**
 * HomePage — Module selection cards landing page.
 */
import type { ModuleId } from '@/types'
import { MODULES } from '@/types'

interface HomePageProps {
  onSelect: (id: ModuleId) => void
}

const MODULE_COLORS: Record<string, string> = {
  blue:    'text-blue-600 bg-blue-50 border-blue-100',
  violet:  'text-violet-600 bg-violet-50 border-violet-100',
  emerald: 'text-emerald-600 bg-emerald-50 border-emerald-100',
  indigo:  'text-indigo-600 bg-indigo-50 border-indigo-100',
  teal:    'text-teal-600 bg-teal-50 border-teal-100',
}

const MODULE_HOVER_COLORS: Record<string, string> = {
  blue:    'hover:border-blue-300',
  violet:  'hover:border-violet-300',
  emerald: 'hover:border-emerald-300',
  indigo:  'hover:border-indigo-300',
  teal:    'hover:border-teal-300',
}

export default function HomePage({ onSelect }: HomePageProps) {
  return (
    <div className="flex flex-col items-center justify-start pt-16 pb-24 px-5 animate-fade-up">
      {/* Hero */}
      <div className="text-center mb-12 max-w-xl">
        <h1 className="text-[2.25rem] sm:text-[2.75rem] font-bold tracking-tight text-navy-900 leading-tight mb-4">
          Intelligence platform<br className="hidden sm:block" /> for go-to-market teams.
        </h1>
        <p className="text-base text-slate-500 leading-relaxed">
          Pick a module to get started — from sourced research reports to
          competitor analysis and AI context documents.
        </p>
      </div>

      {/* Module cards grid */}
      <div className="w-full max-w-4xl grid sm:grid-cols-3 gap-5">
        {MODULES.map((m, i) => (
          <button
            key={m.id}
            onClick={() => onSelect(m.id)}
            className={`module-card text-left animate-fade-up ${MODULE_HOVER_COLORS[m.color] ?? 'hover:border-slate-300'}`}
            style={{ animationDelay: `${i * 80}ms` }}
          >
            {/* Icon badge */}
            <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl text-2xl
              border mb-4 ${MODULE_COLORS[m.color] ?? 'text-slate-600 bg-slate-50 border-slate-200'}`}
              aria-hidden="true"
            >
              {m.icon}
            </div>

            <h2 className="text-base font-semibold text-slate-900 mb-1">{m.label}</h2>
            <p className="text-sm text-slate-500 leading-relaxed">{m.tagline}</p>

            {/* CTA arrow */}
            <div className="mt-5 flex items-center gap-1 text-xs font-medium text-slate-400">
              <span>Open</span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M2 6h8M7 3l3 3-3 3" stroke="currentColor" strokeWidth="1.4"
                      strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}
